import React from "react";
import { ConnectionStatusBadge } from "./status_badges.component";
import { formatDateTime, formatSpeed } from "../services/firestoreTracking.service";

const MetricCard = ({ label, value, badge }) => {
  return <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
    <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">{label}</div>
    <div className="mt-2 flex items-center justify-between gap-3">
      <div className="text-[1.05rem] leading-7 text-[#161311]">{value || "Not available"}</div>
      {badge}
    </div>
  </div>;
};

const speedLimitProviderLabel = (tracking) => {
  const providerName =
    tracking?.summary?.speedingAnalysis?.providerName ||
    tracking?.summary?.providerName ||
    "";
  if (providerName === "tomtom-snap-to-roads") return "TomTom Snap to Roads";
  if (providerName === "google-roads-speed-limits") return "Google Roads fallback";
  return providerName || "Provider not configured";
};

const formatRoadLimit = (tracking) => {
  const number = Number(tracking?.displaySpeedLimitKph ?? tracking?.speedLimitKph);
  if (Number.isFinite(number) && number > 0) {
    return `${formatSpeed(number)}${tracking?.speedLimitEstimated ? " estimated" : ""}`;
  }
  if (tracking?.providerStatus && tracking.providerStatus !== "ready") {
    return tracking.providerStatus.replace(/_/g, " ");
  }
  return tracking?.speedLimitMessage || tracking?.providerMessage || "Lookup pending";
};

const formatStatusText = (value) => {
  const text = (value || "").toString().trim();
  return text ? text.replace(/_/g, " ") : "";
};

const countFromSummary = (value) => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

const currentSpeedingLabel = (tracking, latestSpeedingRecord) => {
  if (latestSpeedingRecord?.statusLabel) return latestSpeedingRecord.statusLabel;
  if (latestSpeedingRecord?.severity) return formatStatusText(latestSpeedingRecord.severity);
  if (tracking?.summary?.latestSpeedingStatus) return formatStatusText(tracking.summary.latestSpeedingStatus);
  if (countFromSummary(tracking?.summary?.confirmedSpeedingCount)) return "confirmed speeding";
  if (countFromSummary(tracking?.summary?.inconclusiveSpeedingCount)) return "inconclusive speeding";
  return formatStatusText(tracking?.summary?.latestGapClassification || tracking?.trackingStatusKey) || "none";
};

const firstPresent = (...values) => values.find((value) => {
  if (value === null || value === undefined) return false;
  return value.toString().trim() !== "";
}) || "";

export const LiveVehiclePanel = ({
  trip,
  preInspection,
  tracking,
  tripStatusLabel,
  compact = false,
  trackingPointCount = 0,
  speedingCount = 0,
  latestSpeedingRecord = null,
  offlineCount = 0,
}) => {
  const summarySpeedingCount = countFromSummary(tracking?.summary?.speedingEventCount);
  const visibleSpeedingCount = speedingCount || summarySpeedingCount;
  const currentBreach = currentSpeedingLabel(tracking, latestSpeedingRecord);
  const vehicleRegistration = firstPresent(
    trip?.vehicleRegNumber,
    trip?.VehicleRegNo,
    trip?.vehicleRegistration,
    trip?.VehicleRegistration,
    trip?.vehicleRegNo,
    trip?.vehicleReg,
    trip?.regNumber,
    trip?.registrationNumber,
    trip?.regsitrationNumber,
    preInspection?.regsitrationNumber,
    preInspection?.registrationNumber,
    preInspection?.vehicleRegNumber,
    preInspection?.VehicleRegNo,
    tracking?.vehicleRegistration
  );
  const vehicleType = firstPresent(
    trip?.vehicleType,
    trip?.VehicleType,
    preInspection?.type,
    preInspection?.vehicleType,
    preInspection?.VehicleType,
    [preInspection?.make, preInspection?.model].filter(Boolean).join(" "),
    tracking?.vehicleType
  );

  return <div className={`rounded-[28px] border border-[#ece5dd] bg-white px-6 py-6 shadow-[0_18px_55px_rgba(15,23,42,0.05)] ${compact ? "" : "h-full"}`}>
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-[1.15rem] font-semibold text-[#161311]">Live Vehicle Telemetry</span>
          <span className="text-sm text-[#6e6258]">Trip context, heartbeat state, road context, and live trip evidence from Firestore.</span>
        </div>
        <div className="rounded-2xl border border-[#f1e1d5] bg-[linear-gradient(135deg,#fff7f1_0%,#ffffff_100%)] px-4 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#a78a76]">Connection State</div>
          <div className="mt-2 flex items-center gap-3">
            <ConnectionStatusBadge status={tracking?.connectionStatus} />
            <div className="text-sm font-medium text-[#3c3129]">{tracking?.trackingStatusLabel || "Tracking unavailable"}</div>
          </div>
        </div>
      </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">Last Telemetry</div>
          <div className="mt-2 text-[1rem] leading-7 text-[#161311]">{formatDateTime(tracking?.lastTelemetryAt)}</div>
        </div>
        <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">Current Road</div>
          <div className="mt-2 text-[1rem] leading-7 text-[#161311]">{tracking?.roadName || "Road lookup pending"}</div>
        </div>
        <div className="rounded-2xl border border-[#ece5dd] bg-[#fcfaf7] px-4 py-4">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c7b6b]">Coordinates</div>
          <div className="mt-2 text-[1rem] leading-7 text-[#161311]">{tracking?.coordinatesLabel || "Not available"}</div>
        </div>
      </div>
    </div>
    <div className="mt-6 grid grid-cols-1 gap-4 xl:grid-cols-2">
      <MetricCard label="Pickup" value={trip?.pickUp || "Not available"} />
      <MetricCard label="Dropoff" value={trip?.dropOff || "Not available"} />
      <MetricCard label="Driver" value={trip?.driver?.user?.name || trip?.driverName || tracking?.driverName || "Driver not assigned"} />
      <MetricCard label="Vehicle" value={vehicleRegistration || "Not available"} />
      <MetricCard label="Live Speed" value={formatSpeed(tracking?.speedKph)} />
      <MetricCard label="Road Limit" value={formatRoadLimit(tracking)} />
      <MetricCard label="Heartbeat" value={formatDateTime(tracking?.summary?.lastHeartbeatAt || tracking?.telemetry?.lastHeartbeatAt || tracking?.lastTelemetryAt)} />
      <MetricCard label="Speed-limit Provider" value={speedLimitProviderLabel(tracking)} />
      <MetricCard label="Tracking Points" value={trackingPointCount ? String(trackingPointCount) : "0"} />
      <MetricCard label="Offline Infringements" value={String(offlineCount || 0)} />
      <MetricCard label="Speeding Feed" value={String(visibleSpeedingCount || 0)} />
      <MetricCard label="Current Breach" value={currentBreach} />
      <MetricCard label="Trip Status" value={tripStatusLabel || tracking?.trackingStatusLabel || "Not available"} />
      <MetricCard label="Vehicle Type" value={vehicleType || "Not available"} />
    </div>
  </div>;
};
